import Backbone from 'backbone';
import ko from 'knockout';
import _ from 'underscore';

import sectionTemplate from './section-template.pug';
import { OptionsSectionViewModel } from './section-vm';
import { AdvancedOptionsView } from './advanced-opt-view';
import { AdvancedOptionsSelectAllView } from './advanced-select-all-opt-view';

/**
 * subSections - array of Section objects, defined as follows:
 *
 * {
 *  advancedOptionsFields // array of field objects used as form config
 *  advancedOptionsSchema // schema corresponding to the fields in advancedOptionsFields
 *  helpId // optional id for pop up to be displayed next to sub-section label
 * }
 *
 * We will use an array to support the scenario where there are multiple advanced options
 * sections (each w/ different schema) under one "Section" (a form-group with hide/show control)
 */

export class OptionsSectionView extends Backbone.View {
  initialize({
    observable,
    field,
    subSectionsConfig,
    sectionLabel,
    description,
    model,
    i18n,
    warningMessage,
    helpId,
    hideAdvancedOptions,
  }) {
    this.viewModel = new OptionsSectionViewModel({ i18n, observable, warningMessage });

    this.advancedOptionsView = [];

    _.each(subSectionsConfig, (subSection) => {
      const viewParams = {
        fields: subSection.advancedOptionsFields,
        schema: subSection.advancedOptionsSchema,
        label: subSection.label,
        model,
        helpId: subSection.helpId,
        i18n,
      };
      const subSectionView = (subSection.includeAllOption)
        ? new AdvancedOptionsSelectAllView(viewParams)
        : new AdvancedOptionsView(viewParams);

      this.advancedOptionsView.push(subSectionView);
    });

    this.property = field.property;
    this.sectionLabel = sectionLabel;
    this.description = description;
    this.warningMessage = warningMessage;
    this.helpId = helpId;
    this.i18n = i18n;
    this.hideAdvancedOptions = hideAdvancedOptions;
  }

  events() {
    return {
      'click .toggle-link': 'triggerToggleEvent',
    };
  }

  triggerToggleEvent() {
    if (!this.hideAdvancedOptions) {
      this.toggleAdvancedOptionsExpansion(!this.viewModel.showAdvancedOptions());
    }
  }

  toggleAdvancedOptionsExpansion(expand) {
    this.viewModel.showAdvancedOptions(!this.hideAdvancedOptions && expand);
  }

  render() {
    const { length } = this.advancedOptionsView;

    this.$el.html(sectionTemplate({
      length,
      sectionLabel: this.sectionLabel,
      description: this.description,
      warningMessage: this.warningMessage,
      helpId: this.helpId,
      i18n: this.i18n,
      hideAdvancedOptions: this.hideAdvancedOptions,
    }));

    ko.applyBindings(this.viewModel, this.el);

    let index = 0;

    _.each(this.advancedOptionsView, (subSection) => {
      this.$(`.advanced-options .section${index}`).html(subSection.render().$el);
      index += 1;
    });

    return this;
  }

  remove() {
    this.advancedOptionsView.remove();
    super.remove();
  }

  getAdvancedOptionsModel() {
    const allSubSectionsModel = {};

    _.each(this.advancedOptionsView, (subSection) => {
      _.extend(allSubSectionsModel, subSection.getCurrentModel());
    });

    return allSubSectionsModel;
  }

  areAllOptionsSelected() {
    const model = this.getAdvancedOptionsModel();

    return _.every(model, prop => prop);
  }

  areAllOptionsDisabled() {
    return _.every(this.advancedOptionsView, subSection => subSection.$('input').attr('disabled') === 'disabled');
  }

  areSomeOptionsSelected() {
    const model = this.getAdvancedOptionsModel();

    return _.some(model, prop => prop);
  }

  selectAllOptions() {
    _.each(this.advancedOptionsView, (subSection) => {
      const { form: { viewModel } } = subSection;

      _.each(subSection.advancedOptionsFields, (field) => {
        viewModel[field.property](true);
      });

      if (subSection.includeAllOption) {
        subSection.toggleAllCheckboxState();
      }
    });
  }

  disableAdvancedOptions() {
    _.each(this.advancedOptionsView, (subSection) => {
      subSection.$('input').attr('disabled', 'disabled');
    });
  }
  enableAdvancedOptions() {
    _.each(this.advancedOptionsView, (subSection) => {
      subSection.$('input').removeAttr('disabled');
    });
  }

  toggleWarningMessage(display) {
    if (this.warningMessage) this.viewModel.displayWarningMessage(display);
  }
}
